'use client';

import { useState, useEffect, useMemo, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useLanguage } from '@/contexts/LanguageContext';

/* deterministic seeded RNG (not used during server render) */
function makeRng(seed: number) {
  let t = seed >>> 0;
  return () => {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

const r2 = (n: number) => Math.round(n * 100) / 100;

const THAI_MONTHS = ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน', 'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'];
const EN_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function formatThaiDate(d: Date) {
  return `วันนี้ ${d.getDate()} ${THAI_MONTHS[d.getMonth()]} พ.ศ. ${d.getFullYear() + 543}`;
}
function formatEnDate(d: Date) {
  return `Today · ${d.getDate()} ${EN_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

const STR = {
  th: {
    tagline: 'ดวงชะตาของคุณรออยู่แล้ว',
    subtitle: 'ค้นพบพลังงานประจำวัน ดวงดาว และเลขมงคลที่จักรวาลเตรียมไว้เฉพาะสำหรับคุณ',
    cta: 'เริ่มดูดวง',
    disclaimer: 'เพื่อความบันเทิงและการไตร่ตรองเท่านั้น',
    pills: ['เลขศาสตร์ & ราศี', 'ไพ่ทาโรต์', 'เลขมงคล', 'สีเสื้อมงคล'],
  },
  en: {
    tagline: 'Your destiny awaits',
    subtitle: 'Discover the daily energy, stars, and lucky numbers the cosmos has prepared just for you',
    cta: 'Begin Reading',
    disclaimer: 'For entertainment and reflection only',
    pills: ['Numerology & Zodiac', 'Tarot', 'Lucky Numbers', 'Lucky Colour'],
  },
} as const;

const FLOAT_GLYPHS = ['☉', '☽', '☿', '♀', '♂', '♃', '♄', '✦', '✧', '✶', '♆', '⚹'];

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@500;600;700&family=Charm:wght@400;700&family=IBM+Plex+Sans+Thai:wght@300;400;500;600&display=swap');

.mo-root{
  position:relative; min-height:100svh; width:100%; overflow:hidden;
  display:flex; flex-direction:column; align-items:center;
  font-family:'IBM Plex Sans Thai','Sarabun',sans-serif;
  color:#F4F1E8;
  background:
    radial-gradient(120% 90% at 84% 82%, rgba(212,168,75,0.16), transparent 46%),
    radial-gradient(90% 70% at 50% 10%, rgba(120,70,170,0.22), transparent 55%),
    radial-gradient(70% 60% at 18% 30%, rgba(14,77,92,0.20), transparent 60%),
    linear-gradient(180deg,#0A0916 0%,#0C0A20 55%,#08070F 100%);
}
.mo-vignette{position:absolute; inset:0; pointer-events:none;
  background:radial-gradient(circle at 50% 42%, transparent 52%, rgba(0,0,0,0.62) 100%);}
.mo-grain{position:absolute; inset:0; pointer-events:none; opacity:.04; mix-blend-mode:overlay;
  background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");}

.mo-fadein{opacity:0; animation:moFade 1s ease forwards;}
.mo-stars{position:absolute; inset:0; pointer-events:none;}
.mo-star{position:absolute; border-radius:50%; background:#F4E4BC;
  box-shadow:0 0 6px rgba(244,228,188,.7); animation:moTwinkle linear infinite;}
.mo-glyphs{position:absolute; inset:0; pointer-events:none; color:#D4A84B;}
.mo-glyph{position:absolute; font-family:'IBM Plex Sans Thai',serif; animation:moDrift ease-in-out infinite;
  text-shadow:0 0 10px rgba(212,168,75,.4);}

.mo-top{position:relative; z-index:5; width:100%; max-width:1180px; display:flex; justify-content:space-between;
  align-items:center; padding:22px 26px;}
.mo-logo{display:flex; align-items:center; gap:10px; font-family:'Cinzel',serif; letter-spacing:.04em;
  font-size:17px; color:#F4E4BC;}
.mo-logo svg{filter:drop-shadow(0 0 8px rgba(212,168,75,.5));}
.mo-lang{display:flex; gap:2px; border:1px solid rgba(212,168,75,.35); border-radius:999px; padding:3px; backdrop-filter:blur(6px);}
.mo-lang button{font:inherit; font-size:12px; letter-spacing:.05em; cursor:pointer; border:0; background:transparent;
  color:rgba(244,241,232,.6); padding:5px 12px; border-radius:999px; transition:.25s;}
.mo-lang button.on{background:linear-gradient(180deg,#E8C77E,#C99A3E); color:#1a1430; font-weight:600;
  box-shadow:0 2px 10px rgba(212,168,75,.4);}

.mo-hero{position:relative; z-index:4; flex:1; width:100%; max-width:720px; padding:0 24px 40px;
  display:flex; flex-direction:column; align-items:center; text-align:center; justify-content:center;}

.mo-orb-wrap{position:relative; width:clamp(230px,40vw,360px); aspect-ratio:1; margin:8px 0 30px;
  animation:moBob 7s ease-in-out infinite;}
.mo-orb-glow{position:absolute; inset:-22%; border-radius:50%; filter:blur(34px); animation:moPulse 6s ease-in-out infinite;
  background:radial-gradient(circle, rgba(150,90,210,.5), rgba(212,168,75,.22) 45%, transparent 70%);}
.mo-hand-wrap{position:absolute; inset:9%; width:82%; height:82%;
  animation:moBreathe 6s ease-in-out infinite;}
.mo-hand{position:absolute; inset:0; width:100%; height:100%; object-fit:contain; display:block;
  filter:drop-shadow(0 0 16px rgba(212,168,75,.40));
  /* Radial alpha mask fades the square edges to true transparency so the page
     (glow + gradients) shows through. Safari/WebKit needs the -webkit- prefix on
     ALL mask sub-properties, not just mask-image, or it fails to render. */
  -webkit-mask-image:radial-gradient(circle at center, #000 40%, transparent 70%);
  mask-image:radial-gradient(circle at center, #000 40%, transparent 70%);
  -webkit-mask-mode:alpha; mask-mode:alpha;
  -webkit-mask-repeat:no-repeat; mask-repeat:no-repeat;
  -webkit-mask-size:100% 100%; mask-size:100% 100%;
  -webkit-mask-position:center; mask-position:center;}

.mo-wordmark{font-family:'Cinzel',serif; font-weight:700; line-height:1; letter-spacing:.01em;
  font-size:clamp(38px,7vw,64px); margin:0;
  background:linear-gradient(180deg,#FBEFCE 0%,#E8C77E 42%,#C99A3E 100%);
  -webkit-background-clip:text; background-clip:text; color:transparent;
  background-size:200% 200%; animation:moSheen 6s ease-in-out infinite;
  filter:drop-shadow(0 2px 18px rgba(212,168,75,.35));}
.mo-tagline{font-family:'Charm',serif; font-weight:700; color:#E8C77E; margin:14px 0 0;
  font-size:clamp(20px,3.4vw,28px); letter-spacing:.02em;}
.mo-subtitle{max-width:30em; margin:14px auto 0; color:rgba(244,241,232,.62);
  font-size:clamp(14px,2vw,16px); line-height:1.7; font-weight:300;}

.mo-date{margin:26px 0 0; padding:8px 18px; border-radius:999px; font-size:13px; letter-spacing:.03em;
  color:#F4E4BC; border:1px solid rgba(212,168,75,.3); background:rgba(212,168,75,.06); backdrop-filter:blur(6px);
  min-height:34px; display:flex; align-items:center;}

.mo-pills{display:flex; flex-wrap:wrap; gap:10px; justify-content:center; margin:22px 0 0;}
.mo-pill{display:flex; align-items:center; gap:7px; padding:8px 14px; border-radius:999px; font-size:12.5px;
  color:#E8C77E; border:1px solid rgba(212,168,75,.28); background:rgba(255,255,255,.02);
  transition:.3s; cursor:default;}
.mo-pill:hover{border-color:rgba(212,168,75,.6); box-shadow:0 0 18px rgba(212,168,75,.22); color:#FBEFCE; transform:translateY(-1px);}

.mo-cta{position:relative; margin:34px 0 0; cursor:pointer; border:0; overflow:hidden;
  font-family:'IBM Plex Sans Thai',sans-serif; font-weight:600; font-size:17px; letter-spacing:.04em;
  color:#241a06; padding:16px 44px; border-radius:14px;
  background:linear-gradient(180deg,#F4D98C 0%,#E0B24F 50%,#C7912F 100%);
  box-shadow:0 10px 30px rgba(199,145,47,.4), inset 0 1px 0 rgba(255,255,255,.5), inset 0 -2px 6px rgba(120,80,10,.4);
  transition:transform .2s, box-shadow .2s;}
.mo-cta:hover{transform:translateY(-2px); box-shadow:0 16px 40px rgba(199,145,47,.5), inset 0 1px 0 rgba(255,255,255,.6);}
.mo-cta:active{transform:translateY(0);}
.mo-cta::after{content:''; position:absolute; top:0; left:-60%; width:40%; height:100%;
  background:linear-gradient(110deg, transparent 20%, rgba(255,255,255,.65) 50%, transparent 80%);
  transform:skewX(-18deg); animation:moShimmer 4.2s ease-in-out infinite;}

.mo-disclaimer{margin:34px 0 6px; font-size:12px; color:rgba(244,241,232,.4); letter-spacing:.03em;}

@keyframes moTwinkle{0%,100%{opacity:.2}50%{opacity:1}}
@keyframes moDrift{0%,100%{transform:translateY(0)}50%{transform:translateY(-16px)}}
@keyframes moBob{0%,100%{transform:translateY(0)}50%{transform:translateY(-12px)}}
@keyframes moPulse{0%,100%{opacity:.7; transform:scale(1)}50%{opacity:1; transform:scale(1.05)}}
@keyframes moBreathe{0%,100%{transform:scale(1)}50%{transform:scale(1.03)}}
@keyframes moSheen{0%,100%{background-position:0% 50%}50%{background-position:100% 50%}}
@keyframes moShimmer{0%{left:-60%}55%,100%{left:130%}}
@keyframes moFade{to{opacity:1}}

@media (prefers-reduced-motion: reduce){
  .mo-fadein,.mo-star,.mo-glyph,.mo-orb-wrap,.mo-orb-glow,.mo-hand-wrap,.mo-wordmark,.mo-cta::after{animation:none !important; opacity:1 !important;}
}
`;

const PillIcon = ({ i }: { i: number }) => {
  const common = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.4, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  if (i === 0)
    return (
      <svg viewBox="0 0 24 24" width="15" height="15" {...common}>
        <circle cx="12" cy="12" r="4.2" />
        {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => {
          const rad = (a * Math.PI) / 180;
          return <line key={a} x1={r2(12 + Math.cos(rad) * 7)} y1={r2(12 + Math.sin(rad) * 7)} x2={r2(12 + Math.cos(rad) * 9.5)} y2={r2(12 + Math.sin(rad) * 9.5)} />;
        })}
      </svg>
    );
  if (i === 1)
    return (
      <svg viewBox="0 0 24 24" width="15" height="15" {...common}>
        <rect x="6.5" y="3.5" width="11" height="17" rx="1.6" />
        <path d="M12 8.5l1.6 2.8h-3.2L12 8.5zM12 15.5l-1.6-2.8h3.2L12 15.5z" />
      </svg>
    );
  if (i === 2)
    return (
      <svg viewBox="0 0 24 24" width="15" height="15" {...common}>
        <circle cx="12" cy="12" r="7.5" />
        <path d="M12 7.5v9M9.5 10h3.2a1.8 1.8 0 010 3.6H9.5" />
      </svg>
    );
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" {...common}>
      <path d="M8 4l-3 2.5 1.6 2.4L8 8v12h8V8l1.4.9L19 6.5 16 4l-2 1.5h-4L8 4z" />
    </svg>
  );
};

export default function WelcomeClient({ onBegin }: { onBegin?: () => void }) {
  const router = useRouter();
  const { lang, setLang } = useLanguage();
  const [mounted, setMounted] = useState(false);
  const tiltRef = useRef<HTMLImageElement>(null);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!mounted) return;
    const onMove = (e: MouseEvent) => {
      const nx = e.clientX / window.innerWidth - 0.5;
      const ny = e.clientY / window.innerHeight - 0.5;
      if (tiltRef.current) tiltRef.current.style.transform = `translate(${nx * 10}px, ${ny * 10}px)`;
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, [mounted]);

  const stars = useMemo(() => {
    const rng = makeRng(20240624);
    return Array.from({ length: 150 }, () => ({
      left: rng() * 100,
      top: rng() * 100,
      size: 0.6 + rng() * 2,
      delay: rng() * 6,
      dur: 2.5 + rng() * 4,
      bright: 0.25 + rng() * 0.6,
    }));
  }, []);

  const glyphs = useMemo(() => {
    const rng = makeRng(78321);
    return Array.from({ length: 14 }, (_, i) => ({
      left: rng() * 100,
      top: rng() * 100,
      size: 11 + rng() * 12,
      delay: rng() * 8,
      dur: 9 + rng() * 8,
      char: FLOAT_GLYPHS[Math.floor(rng() * FLOAT_GLYPHS.length)] ?? '✦',
      op: 0.1 + rng() * 0.18,
      key: i,
    }));
  }, []);

  let t = STR[lang];

  // ---- DEFINITIVE FIX: do not server-render any dynamic UI ----
  // Until mounted on the client, render only the static background. The server render
  // and the first client render are identical, so hydration cannot mismatch.
  if (!mounted) {
    return (
      <div className="mo-root">
        <style dangerouslySetInnerHTML={{ __html: CSS }} />
        <div className="mo-vignette" />
        <div className="mo-grain" />
      </div>
    );
  }

  return (
    <div className="mo-root">
      <style dangerouslySetInnerHTML={{ __html: CSS }} />
      <div className="mo-vignette" />
      <div className="mo-grain" />

      <div className="mo-stars mo-fadein" aria-hidden>
        {stars.map((s, i) => (
          <span
            key={i}
            className="mo-star"
            style={{ left: `${s.left}%`, top: `${s.top}%`, width: s.size, height: s.size, opacity: s.bright, animationDelay: `${s.delay}s`, animationDuration: `${s.dur}s` }}
          />
        ))}
      </div>
      <div className="mo-glyphs mo-fadein" aria-hidden>
        {glyphs.map((g) => (
          <span key={g.key} className="mo-glyph" style={{ left: `${g.left}%`, top: `${g.top}%`, fontSize: g.size, opacity: g.op, animationDelay: `${g.delay}s`, animationDuration: `${g.dur}s` }}>
            {g.char}
          </span>
        ))}
      </div>

      <header className="mo-top mo-fadein">
        <div className="mo-logo">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#E8C77E" strokeWidth="1.6">
            <path d="M16.5 14.5A6.5 6.5 0 119.5 4a5 5 0 007 10.5z" fill="rgba(212,168,75,.15)" />
          </svg>
          MysticOracle
        </div>
        <div className="mo-lang">
          <button className={lang === 'th' ? 'on' : ''} onClick={() => setLang('th')}>ไทย</button>
          <button className={lang === 'en' ? 'on' : ''} onClick={() => setLang('en')}>EN</button>
        </div>
      </header>

      <main className="mo-hero mo-fadein">
        <div className="mo-orb-wrap">
          <div className="mo-orb-glow" />
          <div className="mo-hand-wrap">
            <img ref={tiltRef} className="mo-hand" src="/hand.png" alt="" aria-hidden />
          </div>
        </div>

        <h1 className="mo-wordmark">MysticOracle</h1>
        <p className="mo-tagline">{t.tagline}</p>
        <p className="mo-subtitle">{t.subtitle}</p>

        <div className="mo-date">{lang === 'th' ? formatThaiDate(new Date()) : formatEnDate(new Date())}</div>

        <div className="mo-pills">
          {t.pills.map((p, i) => (
            <span key={i} className="mo-pill">
              <PillIcon i={i} />
              {p}
            </span>
          ))}
        </div>

        <button className="mo-cta" onClick={() => { onBegin?.(); router.push('/name-input-screen'); }}>
          {t.cta} &nbsp;🔮
        </button>

        <p className="mo-disclaimer">{t.disclaimer} ✦</p>
      </main>
    </div>
  );
}